// WorkHoursPage.jsx — SB-034, the member work-hours surface (OQ-C33: members
// write their own hours directly, like the nickname; the charter's write monopoly
// covers documents/source/config/approval, not one's own self-reported data).
//
// Backed by (session-authed, own data only):
//   GET  /api/me/backlogs?projectGroupId  → the picker source: exactly the
//        backlogs the rule engine still accepts (R-04 pending|in_progress,
//        R-29 non-friction), so a selection can never be rejected by those rules.
//   GET  /api/me/work-log?fromDay&toDay   → { rows, tz, today, windowDays, mayLog }
//   POST /api/me/work-log                 → entry (tz taken from the account)
//   POST /api/me/work-log/{id}/reverse    → R-36 correction
//   PATCH  /api/me/work-log/{id}          → R-08 owner edit of a PLANNED row
//   DELETE /api/me/work-log/{id}          → R-08 owner delete of a PLANNED row
//
// The R-01 entry window (today ± (N−1)/2) IS the strip this page renders — one
// concept serving both the affordance and the legality boundary, so a day the
// engine would refuse is never offered. Corrections ship WITH entry on purpose:
// once a row is invested, OQ-C21 forbids admin in-place fixes, so without a
// reversal channel a mistyped number would be permanent dirty data.
//
// 第八十四批（线 B）：补上 R-08 的另一半。此前这一页只有"冲正"一个入口，而冲正
// **明确不受理 planned 行**（R-36），于是录错一笔计划工时在界面上是一条死路。
// 两个入口的分工是硬的、也是互斥的（判据在 plannedEdit.mjs）：
//   · 规划态（planned）→ 改 / 删，到期前本人可为，服务端做替代式追加；
//   · 投入态（invested）→ 只能冲正，因为那种行**删不掉**。
// 所以已投入的行上**绝不出现**删除按钮：一个必然被服务端拒的按钮，会把"这件事
// 做不到"伪装成"你操作失败了"，比没有按钮更坏。
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { errKey, ruleKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { useLang, useT } from '../i18n/index.mjs';
import { buildPlannedPatch, mayEditPlanned, mayReverse } from './plannedEdit.mjs';
import {
  BACKLOG_PICKER_LIMIT, DAY_CAP_MINUTES, backlogPickerHint, entryWindow, hoursLabel, hoursToMinutes, isoLabel,
  netByDay, shortLabel,
} from './workDays.mjs';

export default function WorkHoursPage() {
  const t = useT();
  const { lang } = useLang();
  // 第一百零一批（多项目制）：拣选器的组来自会话上下文，随切换器联动。
  const { currentGroup } = useAuth();
  const [state, setState] = useState({ status: 'loading', error: '' });
  const [sheet, setSheet] = useState({ rows: [], tz: '', today: 0, windowDays: 7, mayLog: false });
  const [backlogs, setBacklogs] = useState([]);
  // SB-159：选择器那一次取数的结局（失败／还有更多），喂 backlogPickerHint。
  const [backlogMeta, setBacklogMeta] = useState({ error: null, hasMore: false, limit: undefined });
  const pickerHint = backlogPickerHint({ ...backlogMeta, rows: backlogs });
  const { snack, notify, clearSnack } = useNotify();

  // entry form
  const [day, setDay] = useState(0);
  const [backlogNo, setBacklogNo] = useState('');
  const [hours, setHours] = useState('1');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  // reversal dialog
  const [reversing, setReversing] = useState(null);   // the row being corrected
  const [revHours, setRevHours] = useState('');
  const [revReason, setRevReason] = useState('');

  // R-08 dialogs (batch 84): edit and delete, both planned-only.
  const [editing, setEditing] = useState(null);       // the planned row being edited
  const [editDay, setEditDay] = useState(0);
  const [editHours, setEditHours] = useState('');
  const [editError, setEditError] = useState('');
  const [deleting, setDeleting] = useState(null);     // the planned row being withdrawn

  const days = useMemo(
    () => (sheet.today ? entryWindow(sheet.today, sheet.windowDays) : []),
    [sheet.today, sheet.windowDays],
  );
  const net = useMemo(() => netByDay(sheet.rows), [sheet.rows]);

  const load = useCallback(async () => {
    setState({ status: 'loading', error: '' });
    try {
      // No date args: the backend defaults to the R-01 window centred on the
      // OWNER's today, so the account's timezone stays the single authority and
      // the browser's clock/zone never enters the calculation.
      const full = await api.myWorkLog();
      setSheet(full);
      const window = entryWindow(full.today, full.windowDays);
      setDay((d) => (d && window.includes(d) ? d : full.today));
      // Optional picker — degrades to an empty backlog list when no group.
      const gid = currentGroup?.id;
      if (gid != null) {
        // OQ-A19: localized titles for the picker (and, via backlogTitleOf, the
        // timesheet rows — /api/me/work-log itself stays language-free).
        // SB-159（第三百八十二批）：取不到 ≠ 没有。失败不再被吞成空表（那会印出「没有可登记的
        // 工单」），而是记下来交给 backlogPickerHint；页长取服务端上限并看 hasMore。
        let bl = null;
        let blErr = null;
        try {
          bl = await api.myBacklogs(gid, { lang, limit: BACKLOG_PICKER_LIMIT });
        } catch (e) {
          blErr = e;
        }
        setBacklogs(Array.isArray(bl?.backlogs) ? bl.backlogs : []);
        setBacklogMeta({ error: blErr, hasMore: bl?.hasMore === true, limit: bl?.limit });
      }
      setState({ status: 'ready', error: '' });
    } catch (e) {
      setState({ status: 'error', error: errMsg(e, t) });
    }
  }, [t, lang, currentGroup?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const minutes = hoursToMinutes(hours);                  // mirrors R-05 locally
    if (!backlogNo) { setFormError(t('work.error.pickBacklog')); return; }
    if (minutes == null) {
      setFormError(t('work.error.halfHour'));
      return;
    }
    setBusy(true);
    try {
      await api.logWork({ backlogNo, workDay: day, minutes, note: note.trim() });
      setNote('');
      notify(t('work.logged'), 'success');
      await load();
    } catch (err) {
      // Batch 43 (language rule): the stable R-xx id in the 400 message maps to
      // an i18n key; unknown rules keep the raw English text (never vanish).
      setFormError(err instanceof ApiError && err.status === 400
        ? localizedRule(err, t)
        : errMsg(err, t));
    } finally {
      setBusy(false);
    }
  };

  const onReverse = async () => {
    const minutes = hoursToMinutes(revHours);                 // same R-05 mirror as entry
    if (minutes == null) { notify(t('work.error.halfHour'), 'error'); return; }
    setBusy(true);
    try {
      await api.reverseWorkLog(reversing.id, minutes, revReason.trim());
      setReversing(null);
      setRevHours('');
      setRevReason('');
      notify(t('work.reversed'), 'success');
      await load();
    } catch (err) {
      notify(err instanceof ApiError && err.status === 400
        ? localizedRule(err, t)
        : errMsg(err, t), 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── R-08 改：替代式追加由服务端完成，这里只发出"变了哪些字段" ──────────────
  const openEdit = (r) => {
    setEditing(r);
    setEditDay(r.workDay);
    setEditHours(hoursLabel(r.minutes));
    setEditError('');
  };

  const onEdit = async () => {
    const minutes = hoursToMinutes(editHours);          // 与录入表单同一个 R-05 校验器
    const { patch, error } = buildPlannedPatch(editing, { day: editDay, minutes });
    if (error) { setEditError(t(error)); return; }
    setBusy(true);
    try {
      await api.editPlannedWorkLog(editing.id, patch);
      setEditing(null);
      notify(t('work.edited'), 'success');
      await load();
    } catch (err) {
      setEditError(workErrText(err, t));
    } finally {
      setBusy(false);
    }
  };

  // ── R-08 删：语义是"撤掉这条计划"。库里那一行会被置 released 并留下审计行
  // （R-30 永不物删）——所以确认框说的是"不再计入"，不是"抹掉记录"。
  const onDelete = async () => {
    setBusy(true);
    try {
      await api.deletePlannedWorkLog(deleting.id);
      setDeleting(null);
      notify(t('work.deleted'), 'success');
      await load();
    } catch (err) {
      notify(workErrText(err, t), 'error');
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (state.status === 'error') {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>
        <Button size="small" onClick={load}>{t('common.retry')}</Button>
      </Box>
    );
  }

  const dayRows = sheet.rows.filter((r) => r.workDay === day);
  const dayNet = net[day] || 0;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('work.title')}
        <HelpLink section="work" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('work.subtitle', { tz: sheet.tz, days: sheet.windowDays })}
      </Typography>

      {!sheet.mayLog ? (
        <Alert severity="info" sx={{ mb: 2 }}>{t('work.noRight')}</Alert>
      ) : null}

      {/* ── the R-01 window strip: also the legality boundary ─────────────── */}
      <Paper variant="outlined" sx={{ p: 1, mb: 2, overflowX: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ minWidth: 'max-content' }}>
          {days.map((d) => {
            const isToday = d === sheet.today;
            const selected = d === day;
            const m = net[d] || 0;
            return (
              <Button
                key={d}
                onClick={() => setDay(d)}
                variant={selected ? 'contained' : 'outlined'}
                sx={{ minWidth: 92, flexDirection: 'column', py: 1, textTransform: 'none' }}
              >
                <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 400 }}>
                  {shortLabel(d)}{isToday ? ` · ${t('work.today')}` : ''}
                </Typography>
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  {m === 0 ? '0' : t('work.hoursValue', { n: hoursLabel(m) })}
                </Typography>
              </Button>
            );
          })}
        </Stack>
      </Paper>

      {/* ── entry ─────────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t('work.addTitle', { day: isoLabel(day) })}
          </Typography>
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack sx={{ alignItems: { md: 'flex-start' } }} direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label={t('work.field.backlog')}
                value={backlogNo}
                onChange={(e) => setBacklogNo(e.target.value)}
                disabled={busy || !sheet.mayLog || backlogs.length === 0}
                fullWidth
                helperText={pickerHint ? t(pickerHint.key, pickerHint.params) : ' '}
                error={pickerHint?.key === 'work.backlogsLoadFailed'}
              >
                {backlogs.map((b) => (
                  // whiteSpace normal：Popover 纸面横向只裁不滚，长标题在 360px 下会被静默裁掉
                  <MenuItem key={b.no} value={b.no} sx={{ whiteSpace: 'normal' }}>
                    {b.no} · {b.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('work.field.hours')}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                disabled={busy || !sheet.mayLog}
                type="number"
                slotProps={{ htmlInput: { step: 0.5, min: 0.5, max: 16 } }}
                sx={{ width: { xs: '100%', md: 160 } }}
                helperText={t('work.field.hours.helper')}
              />
              <TextField
                label={t('work.field.note')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy || !sheet.mayLog}
                fullWidth
                helperText=" "
              />
              <Button
                type="submit"
                variant="contained"
                disabled={busy || !sheet.mayLog}
                sx={{ whiteSpace: 'nowrap', py: 1.75 }}
              >
                {t('work.action.log')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('work.dayTotal', {
                used: t('work.hoursValue', { n: hoursLabel(dayNet) }),
                cap: t('work.hoursValue', { n: hoursLabel(DAY_CAP_MINUTES) }),
              })}
            </Typography>
            {formError ? <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert> : null}
          </Box>
        </CardContent>
      </Card>

      {/* ── the selected day's rows ───────────────────────────────────────── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {t('work.rowsTitle', { day: isoLabel(day) })}
      </Typography>
      {dayRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('work.noRows')}</Typography>
      ) : (
        <DataTable minWidth={640}>
            <TableHead>
              <TableRow>
                <TableCell>{t('work.col.backlog')}</TableCell>
                <TableCell align="right">{t('work.col.hours')}</TableCell>
                <TableCell>{t('work.col.status')}</TableCell>
                <TableCell align="right">{t('work.col.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dayRows.map((r) => {
                // 两个入口互斥，判据在 plannedEdit.mjs：投入行只能冲正（R-36），
                // 规划行只能改/删（R-08）。**已投入的行上不出现删除按钮。**
                const correctable = mayReverse(r, sheet.mayLog);
                const editable = mayEditPlanned(r, sheet.mayLog);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {r.backlogNo} · {backlogTitleOf(r, backlogs)}
                      {r.direction === -1 ? (
                        <Chip size="small" variant="outlined" color="warning"
                              label={t('work.reversalRow')} sx={{ ml: 1 }} />
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={{ color: r.direction === -1 ? 'warning.main' : undefined }}>
                      {r.direction === -1 ? '−' : ''}
                      {t('work.hoursValue', { n: hoursLabel(r.minutes) })}
                    </TableCell>
                    <TableCell>
                      {/* t(k)===k guard: an unknown status must not render a dotted key */}
                      <Chip
                        size="small"
                        label={t(`work.status.${r.status}`) === `work.status.${r.status}`
                          ? r.status
                          : t(`work.status.${r.status}`)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {correctable ? (
                        <Button size="small" disabled={busy}
                                onClick={() => { setReversing(r); setRevHours(''); setRevReason(''); }}>
                          {t('work.action.reverse')}
                        </Button>
                      ) : null}
                      {editable ? (
                        <>
                          <Button size="small" disabled={busy} onClick={() => openEdit(r)}>
                            {t('work.action.edit')}
                          </Button>
                          <Button size="small" color="warning" disabled={busy}
                                  onClick={() => setDeleting(r)}>
                            {t('work.action.delete')}
                          </Button>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
        </DataTable>
      )}

      {/* ── R-36 correction dialog ────────────────────────────────────────── */}
      <Dialog open={Boolean(reversing)} onClose={() => (busy ? null : setReversing(null))}
              fullWidth maxWidth="xs">
        <DialogTitle>{t('work.reverse.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('work.reverse.desc')}
          </Typography>
          {reversing ? (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {reversing.backlogNo} · {isoLabel(reversing.workDay)} ·{' '}
              {t('work.hoursValue', { n: hoursLabel(reversing.minutes) })}
            </Typography>
          ) : null}
          <TextField
            autoFocus fullWidth margin="dense" type="number"
            label={t('work.reverse.hours')}
            value={revHours}
            onChange={(e) => setRevHours(e.target.value)}
            disabled={busy}
            slotProps={{ htmlInput: { step: 0.5, min: 0.5, max: reversing ? reversing.minutes / 60 : 16 } }}
          />
          <TextField
            fullWidth margin="dense"
            label={t('work.reverse.reason')}
            value={revReason}
            onChange={(e) => setRevReason(e.target.value)}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReversing(null)} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={onReverse} disabled={busy}>
            {t('work.action.reverse')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── R-08 编辑规划工时 ─────────────────────────────────────────────── */}
      <Dialog open={Boolean(editing)} onClose={() => (busy ? null : setEditing(null))}
              fullWidth maxWidth="xs">
        <DialogTitle>{t('work.edit.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('work.edit.desc')}
          </Typography>
          {editing ? (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {editing.backlogNo} · {isoLabel(editing.workDay)} ·{' '}
              {t('work.hoursValue', { n: hoursLabel(editing.minutes) })}
            </Typography>
          ) : null}
          {/* 日期只在 R-01 窗口内选：窗口条本身就是合法边界，这里复用同一份天数，
              于是页面永远不会提供一个引擎必然拒绝的日子。 */}
          <TextField
            select fullWidth margin="dense"
            label={t('work.edit.day')}
            value={days.includes(editDay) ? editDay : ''}
            onChange={(e) => setEditDay(Number(e.target.value))}
            disabled={busy}
          >
            {days.map((dd) => (
              <MenuItem key={dd} value={dd}>
                {isoLabel(dd)}{dd === sheet.today ? ` · ${t('work.today')}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth margin="dense" type="number"
            label={t('work.edit.hours')}
            value={editHours}
            onChange={(e) => setEditHours(e.target.value)}
            disabled={busy}
            slotProps={{ htmlInput: { step: 0.5, min: 0.5, max: 16 } }}
            helperText={t('work.field.hours.helper')}
          />
          {editError ? <Alert severity="error" sx={{ mt: 1 }}>{editError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={onEdit} disabled={busy}>
            {t('work.action.edit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── R-08 删除规划工时 ─────────────────────────────────────────────── */}
      <Dialog open={Boolean(deleting)} onClose={() => (busy ? null : setDeleting(null))}
              fullWidth maxWidth="xs">
        <DialogTitle>{t('work.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('work.delete.desc')}
          </Typography>
          {deleting ? (
            <Typography variant="body2">
              {deleting.backlogNo} · {isoLabel(deleting.workDay)} ·{' '}
              {t('work.hoursValue', { n: hoursLabel(deleting.minutes) })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={onDelete} disabled={busy}>
            {t('work.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// Page-specific spec for the shared mapper (SB-042): 403 here means the job
// post lacks log_work_hours.
function errMsg(e, t) {
  return t(errKey(e, { byStatus: { 403: 'work.noRight' }, fallback: 'work.error.generic' }));
}

// OQ-A19: the timesheet rows carry the server's language-free backlogTitle;
// when the (localized) picker knows the same backlog, prefer its title.
function backlogTitleOf(row, backlogs) {
  const hit = backlogs.find((b) => b.no === row.backlogNo);
  return hit ? hit.title : row.backlogTitle;
}

// Batch 43 (language rule): map the stable R-xx id in a 400 message to the
// member's language; an unknown rule keeps the raw English text (never vanish).
function localizedRule(e, t) {
  const rk = ruleKey(e);
  if (rk && t(rk) !== rk) return t(rk);
  return e.message || t('work.error.generic');
}

// 第八十四批：R-08 两条端点的三种拒绝各有**自己的错误码**，因为它们要说的是三件
// 不同的事——"这行已转投入，只能冲正"必须指路，"这不是你的记录"必须什么都不说。
// 走码而不是解析报文：`ruleKey` 认的是 `^R-xx:` 前缀，而 `R-08/R-09:` 那一句根本
// 匹配不上——把 HTTP 语义挂在报文措辞上，改一个词就换一种表现，且没有判据会响。
function workErrText(e, t) {
  const key = errKey(e, {
    byCode: {
      ALREADY_INVESTED: 'work.error.alreadyInvested',
      PLAN_DUE: 'work.error.planDue',
      NOT_PLANNED: 'work.error.notPlanned',
    },
    byStatus: { 403: 'work.noRight', 404: 'work.error.gone' },
    fallback: '',
  });
  if (key) return t(key);
  if (e instanceof ApiError && e.status === 400) return localizedRule(e, t);
  return errMsg(e, t);
}
